import { create } from 'zustand';

type FoundrySelectionStore = {
  itemIdToCategoryPath: Record<string, string>;
  categoryPathToOptions: Record<string, { id: string; name: string }[]>;
  loadingCategories: Set<string>;
  setFoundryCategoryForItem: (itemId: string, categoryPath: string) => void;
  getCategoryPathForItem: (itemId: string) => string | undefined;
  setOptionsForCategory: (categoryPath: string, options: { id: string; name: string }[]) => void;
  getOptionsForCategory: (categoryPath: string) => { id: string; name: string }[] | undefined;
  setLoadingCategory: (categoryPath: string, loading: boolean) => void;
  isCategoryLoading: (categoryPath: string) => boolean;
  preloadFoundryItemsForAssembly: (assemblyItems: any[]) => Promise<void>;
};

export const useFoundrySelectionStore = create<FoundrySelectionStore>((set, get) => ({
  itemIdToCategoryPath: {},
  categoryPathToOptions: {},
  loadingCategories: new Set(),
  setFoundryCategoryForItem: (itemId: string, categoryPath: string) =>
    set({ itemIdToCategoryPath: { ...get().itemIdToCategoryPath, [itemId]: categoryPath } }),
  getCategoryPathForItem: (itemId: string) => get().itemIdToCategoryPath[itemId],
  setOptionsForCategory: (categoryPath: string, options) =>
    set({ categoryPathToOptions: { ...get().categoryPathToOptions, [categoryPath]: options } }),
  getOptionsForCategory: (categoryPath: string) => get().categoryPathToOptions[categoryPath],
  setLoadingCategory: (categoryPath: string, loading: boolean) =>
    set(state => ({
      loadingCategories: loading
        ? new Set([...state.loadingCategories, categoryPath])
        : new Set([...state.loadingCategories].filter(cat => cat !== categoryPath)),
    })),
  isCategoryLoading: (categoryPath: string) => get().loadingCategories.has(categoryPath),
  preloadFoundryItemsForAssembly: async (assemblyItems: any[]) => {
    const {
      getCategoryPathForItem,
      getOptionsForCategory,
      setOptionsForCategory,
      setLoadingCategory,
    } = get();

    // Find all unique category paths for foundry items in the assembly
    const categoryPaths = new Set<string>();

    assemblyItems.forEach(item => {
      const isFoundryItem =
        (item as any)?.isfoundryitem === true ||
        (item as any)?.attribute?.attributevaluename?.includes('Foundry Item');

      if (isFoundryItem) {
        const categoryPath = getCategoryPathForItem((item as any)?.itemid);
        if (categoryPath) {
          categoryPaths.add(categoryPath);
        }
        // Note: We don't add a fallback path here since we don't know the actual category
        // The component will handle this case by only showing the current item
      }
    });

    // Preload items for each category path
    const preloadPromises = Array.from(categoryPaths).map(async categoryPath => {
      // Skip if already loaded or loading
      if (getOptionsForCategory(categoryPath) || get().isCategoryLoading(categoryPath)) {
        return;
      }

      setLoadingCategory(categoryPath, true);

      try {
        const { BuildFoundryService } = await import('../api/BuildFoundryService');
        const res = await BuildFoundryService.getFoundryItemsByCategoryPath(categoryPath);
        const options = (res.dataList || []).map((x: any) => ({ id: x.id, name: x.name }));
        setOptionsForCategory(categoryPath, options);
        console.log(
          `🔧 Successfully preloaded ${options.length} foundry items for category: ${categoryPath}`
        );
      } catch (err) {
        console.warn(`⚠️ Failed to preload foundry items for category: ${categoryPath}`, err);
        // Don't cache failed requests to avoid repeated API calls
      } finally {
        setLoadingCategory(categoryPath, false);
      }
    });

    await Promise.all(preloadPromises);
  },
}));
